import { DataLogStatus, SubmissionUpdateField } from 'store/app/enums';
import { TablePermission } from 'store/api/enums';
import { VatReturnLog } from 'store/vat/models';
import { calculateNextDate } from 'utils/calculate-next-date';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { sortVatReturnLogs } from 'utils/sort-vat-return-logs';
import { Organisation, SelectedEntityAccounts } from '../models';
import { UPDATE_ENTITY_MEMBERS } from '../constants';
import {
  CompleteScanStepsSuccessAction,
  FetchAccountsSuccessAction,
  FetchOrganisationContactsSuccessAction,
  FetchOrganisationsSuccessAction,
  OrganisationState,
  UpdateEntityMembersAction,
  UpdateEntityMembersBulkAction,
  UpdateOrganisationTaxRatesAction,
  UpdateOrganisationTotalTaxBalanceAction,
  UpdateSubmissionLogAction,
  UploadOrganisationAttachmentSuccessAction,
} from '../types';
import { addEntitiesMapping, addMapping } from './mapping';
import { applyPendingTrackingCategories } from './excelHandlers';

export function handleFetchOrganisationsSuccess(
  state: OrganisationState,
  action: FetchOrganisationsSuccessAction
): OrganisationState {
  const { organisations, mappings } = action.payload;
  const maps = mappings;
  const filteredOrganisations = organisations?.filter(
    (org) => org.providerName?.toLowerCase() !== 'hmrc'
  );
  // Merge incoming organisations with any locally enriched fields (taxRates, chartOfAccounts,
  // excelFilesMetadata, users with profile pictures, etc.) from the current state.
  // This is especially important for the lightweight organisations refresh, which does not
  // re-populate these client-side enrichments.
  const orgs =
    filteredOrganisations?.map((org) => {
      const previous = state.organisations?.find((p) => p.uuid === org.uuid);
      if (!previous) {
        return org;
      }

      const merged: Organisation = {
        ...org,
      };

      // Preserve excelFilesMetadata when the incoming payload has none.
      const hasIncomingExcelFiles =
        (org as any).excelFilesMetadata !== undefined &&
        Array.isArray((org as any).excelFilesMetadata) &&
        (org as any).excelFilesMetadata.length > 0;
      if (!hasIncomingExcelFiles && (previous as any).excelFilesMetadata) {
        (merged as any).excelFilesMetadata = (
          previous as any
        ).excelFilesMetadata;
      }

      // Preserve taxRates / taxEnabled when not provided in the incoming payload.
      if (
        (merged as any).taxRates == null &&
        (previous as any).taxRates !== undefined
      ) {
        (merged as any).taxRates = (previous as any).taxRates;
      }
      if (
        (merged as any).taxEnabled == null &&
        (previous as any).taxEnabled !== undefined
      ) {
        (merged as any).taxEnabled = (previous as any).taxEnabled;
      }
      if (
        (merged as any).taxRatesUnique == null &&
        (previous as any).taxRatesUnique !== undefined
      ) {
        (merged as any).taxRatesUnique = (previous as any).taxRatesUnique;
      }

      // Preserve chartOfAccounts when we've already fetched them locally;
      // organisations list API often omits COA or sends [] while names live on the org from fetchChartOfAccounts.
      const prevCoa = (previous as any).chartOfAccounts;
      const incomingCoa = (org as any).chartOfAccounts;
      const prevCoaLoaded = (previous as any).chartOfAccountsLoaded;
      const incomingCoaLoaded = (org as any).chartOfAccountsLoaded;
      if (
        Array.isArray(prevCoa) &&
        prevCoa.length > 0 &&
        (!Array.isArray(incomingCoa) || incomingCoa.length === 0)
      ) {
        (merged as any).chartOfAccounts = prevCoa;
      }
      if (incomingCoaLoaded == null && prevCoaLoaded !== undefined) {
        (merged as any).chartOfAccountsLoaded = prevCoaLoaded;
      }

      // Preserve trackingCategories (client-side from fetchTrackingCategories); organisations API does not return them.
      // Include empty arrays — they mean "fetched, none exist" and must not be cleared on org refresh.
      const prevTracking = (previous as any).trackingCategories;
      const incomingTracking = (org as any).trackingCategories;
      if (
        prevTracking !== undefined &&
        incomingTracking === undefined
      ) {
        (merged as any).trackingCategories = prevTracking;
      }

      // Preserve transactionItems (client-side from fetchTransactionItems); organisations API does not return them.
      const prevItems = (previous as any).transactionItems;
      const incomingItems = (org as any).transactionItems;
      if (prevItems !== undefined && incomingItems === undefined) {
        (merged as any).transactionItems = prevItems;
      }

      // Preserve users array (including any attached profile pictures) when present.
      if (Array.isArray(previous.users) && previous.users.length > 0) {
        merged.users = previous.users;
      }

      return merged;
    }) ?? filteredOrganisations;
  const applied = applyPendingTrackingCategories(
    orgs,
    state.pendingTrackingCategoriesByOrgId
  );
  const orgsWithTracking = applied.organisations;
  const mappedReturns = maps ? addMapping(orgsWithTracking, maps) : undefined;

  const mappedOrganisations = orgsWithTracking
    ? addEntitiesMapping(orgsWithTracking, maps)
    : undefined;
  return {
    ...state,
    loading: false,
    organisations: orgsWithTracking,
    mappings: mappings,
    mappedOrganisations,
    mappedReturns,
    pendingTrackingCategoriesByOrgId: applied.pending,
  };
}

export function handleUpdateOrganisationTaxRates(
  state: OrganisationState,
  action: UpdateOrganisationTaxRatesAction
): OrganisationState {
  const { connectionId, taxRates, taxEnabled, taxRatesUnique } = action.payload;

  const organisations = state.organisations?.map((org) => {
    if (org.connectionId !== connectionId) {
      return org;
    }
    return {
      ...org,
      taxRates,
      taxEnabled,
      ...(taxRatesUnique !== undefined ? { taxRatesUnique } : {}),
      hasTaxRates: (taxRates?.length ?? 0) > 0,
    };
  });

  const mappings = state.mappings;
  const mappedReturns = mappings
    ? addMapping(organisations, mappings)
    : undefined;
  const mappedOrganisations = organisations
    ? addEntitiesMapping(organisations, mappings)
    : undefined;

  return {
    ...state,
    organisations,
    mappedOrganisations,
    mappedReturns,
  };
}

export function handleUpdateOrganisationTotalTaxBalance(
  state: OrganisationState,
  action: UpdateOrganisationTotalTaxBalanceAction
): OrganisationState {
  const { organisationId, totalTaxBalance } = action.payload;
  if (!state.organisations) return state;

  const organisations = state.organisations.map((org) =>
    org.uuid === organisationId
      ? {
          ...org,
          organisationDetails: {
            ...org.organisationDetails,
            totalTaxBalance,
          },
        }
      : org
  );

  return {
    ...state,
    organisations,
  };
}

export function handleFetchOrganisationContactsRequest(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    loading: true,
    organisationViesContacts: undefined,
  };
}

export function handleFetchOrganisationContactsSuccess(
  state: OrganisationState,
  action: FetchOrganisationContactsSuccessAction
): OrganisationState {
  const { connectionId, contacts } = action.payload;
  return {
    ...state,
    loading: false,
    organisationViesContacts: { connectionId, contacts },
  };
}

export function handleFetchOrganisationContactsFailure(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    loading: false,
    organisationViesContacts: undefined,
  };
}

export function handleFetchAccountsRequest(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    loading: true,
    selectedEntityAccounts: undefined,
  };
}

export function handleFetchAccountsSuccess(
  state: OrganisationState,
  action: FetchAccountsSuccessAction
): OrganisationState {
  const { connectionId, entityAccounts } = action.payload;
  entityAccounts.sort((a, b) => a.name.localeCompare(b.name));

  const selectedEntityAccounts: SelectedEntityAccounts = {
    connectionId,
    entityAccounts,
  };

  return {
    ...state,
    loading: false,
    selectedEntityAccounts,
  };
}

export function handleFetchAccountsFailure(
  state: OrganisationState
): OrganisationState {
  return {
    ...state,
    loading: false,
    selectedEntityAccounts: undefined,
  };
}

export function handleUpdateEntityMembers(
  state: OrganisationState,
  action: UpdateEntityMembersAction
): OrganisationState {
  const { organisationId, member, tablePermission } = action.payload;
  if (state.organisations && state.mappedOrganisations) {
    const organisations = [...state.organisations];
    const mappedOrganisations = [...state.mappedOrganisations];
    const orgIdx = organisations?.findIndex((o) => o.uuid === organisationId);
    const mapOrgIdx = mappedOrganisations?.findIndex(
      (mo) => mo.organisationId === organisationId
    );

    if (orgIdx > -1 && mapOrgIdx > -1) {
      const newUsers = state.organisations[orgIdx].users;
      if (tablePermission === TablePermission.ALL) {
        if (
          newUsers.findIndex((nu) => nu.accountUuid === member.accountUuid) ===
          -1
        )
          newUsers.push(member);
      } else if (tablePermission === TablePermission.NONE) {
        const idx = newUsers.findIndex(
          (nu) => nu.accountUuid === member.accountUuid
        );
        idx > -1 && newUsers.splice(idx, 1);
      }

      organisations[orgIdx].users = newUsers;
      mappedOrganisations[mapOrgIdx].users = newUsers;
    }

    return {
      ...state,
      organisations,
      mappedOrganisations,
    };
  }
  return {
    ...state,
  };
}

export function handleUpdateEntityMembersBulk(
  state: OrganisationState,
  action: UpdateEntityMembersBulkAction
): OrganisationState {
  const { items } = action.payload;
  let newState = state;
  for (const item of items) {
    newState = handleUpdateEntityMembers(newState, {
      type: UPDATE_ENTITY_MEMBERS,
      payload: item,
    } as UpdateEntityMembersAction);
  }

  return newState;
}

export function handleUpdateSubmissionLog(
  state: OrganisationState,
  action: UpdateSubmissionLogAction
): OrganisationState {
  const { mappingId, submissionUpdateField, data, dataLogId } = action.payload;
  const normalizeId = (value: unknown): string =>
    String(value ?? '')
      .trim()
      .toLowerCase();
  const normalizedDataLogId = normalizeId(dataLogId);
  const toStatusKey = (value: unknown): string => {
    const str = String(value ?? '').trim();
    if (!str) return '';
    const maybeEnumKey = str as keyof typeof DataLogStatus;
    if (DataLogStatus[maybeEnumKey] !== undefined) {
      return maybeEnumKey;
    }
    const matched = (
      Object.entries(DataLogStatus) as [keyof typeof DataLogStatus, string][]
    ).find(([, enumValue]) => enumValue === str);
    return matched ? String(matched[0]) : str;
  };

  if (!state.mappings) {
    return {
      ...state,
    };
  }

  const mappings = [...state.mappings];
  const mapIdx = mappings.findIndex((m) => m.mappingId === mappingId);
  if (mapIdx === -1) {
    return {
      ...state,
    };
  }

  const mapping = mappings[mapIdx];
  const dataLogs = [...(mapping.dataLogs || [])];
  const dataLogIdx = dataLogs.findIndex(
    (dl) => normalizeId(dl.uuid) === normalizedDataLogId
  );
  const getMappingPeriodDates = () => {
    const fromReportingDate = mapping.periodFromDate;
    const calculated = fromReportingDate
      ? calculateNextPeriod(fromReportingDate, mapping.returnFrequency || '')
      : [];
    const toReportingDate = calculated.length > 1 ? calculated[1] : undefined;
    return { fromReportingDate, toReportingDate };
  };

  const upsertDataLog = (updater: (current: VatReturnLog) => VatReturnLog) => {
    if (dataLogIdx > -1) {
      dataLogs[dataLogIdx] = updater(dataLogs[dataLogIdx]);
      return;
    }
    if (!normalizedDataLogId) {
      return;
    }
    const seed = {
      uuid: dataLogId,
      mappingId,
      ...getMappingPeriodDates(),
    } as unknown as VatReturnLog;
    dataLogs.push(updater(seed));
  };

  switch (submissionUpdateField) {
    case SubmissionUpdateField.STATUS: {
      const statusKey = toStatusKey(data);
      if (!statusKey) {
        break;
      }

      upsertDataLog((current) => ({
        ...current,
        status: statusKey,
      }));

      // Shift mapping-level dates when a submission is completed.
      if (statusKey === 'SUBMITTED') {
        const nextFromDate = calculateNextDate(
          mapping.periodFromDate,
          mapping.returnFrequency || ''
        );
        const nextDueDate = calculateNextDate(
          mapping.returnDueDate,
          mapping.returnFrequency || ''
        );
        mappings[mapIdx] = {
          ...mapping,
          periodFromDate: nextFromDate,
          returnDueDate: nextDueDate,
          dataLogs,
        };
      } else {
        mappings[mapIdx] = {
          ...mapping,
          dataLogs,
        };
      }
      break;
    }
    case SubmissionUpdateField.PREPARER:
      if (dataLogIdx > -1) {
        dataLogs[dataLogIdx] = {
          ...dataLogs[dataLogIdx],
          preparerUser: data,
          savedByAccountId: data?.accountUuid,
          savedByUserId: data?.userUuid,
        };
      }
      mappings[mapIdx] = { ...mapping, dataLogs };
      break;
    case SubmissionUpdateField.REVIEWER:
      if (dataLogIdx > -1) {
        dataLogs[dataLogIdx] = {
          ...dataLogs[dataLogIdx],
          reviewerUser: data,
          reviewerAccountId: data?.accountUuid ?? null,
          reviewerUserId: data?.userUuid ?? null,
        };
      }
      mappings[mapIdx] = { ...mapping, dataLogs };
      break;
    case SubmissionUpdateField.CLIENT:
      if (dataLogIdx > -1) {
        dataLogs[dataLogIdx] = {
          ...dataLogs[dataLogIdx],
          clientUser: data,
          clientAccountId: data?.accountUuid ?? null,
          clientUserId: data?.userUuid ?? null,
        };
      }
      mappings[mapIdx] = { ...mapping, dataLogs };
      break;
    case SubmissionUpdateField.REMOVE_REVIEWER:
      if (dataLogIdx > -1) {
        dataLogs[dataLogIdx] = {
          ...dataLogs[dataLogIdx],
          reviewerUser: undefined,
          reviewerAccountId: null,
          reviewerUserId: null,
        };
      }
      mappings[mapIdx] = { ...mapping, dataLogs };
      break;
    case SubmissionUpdateField.REMOVE_CLIENT:
      if (dataLogIdx > -1) {
        dataLogs[dataLogIdx] = {
          ...dataLogs[dataLogIdx],
          clientUser: undefined,
          clientAccountId: null,
          clientUserId: null,
        };
      }
      mappings[mapIdx] = { ...mapping, dataLogs };
      break;
  }

  const sortedMappings = mappings.map((m) =>
    m.dataLogs?.length
      ? { ...m, dataLogs: sortVatReturnLogs(m.dataLogs) }
      : m
  );

  return {
    ...state,
    mappings: sortedMappings,
    mappedReturns: state.organisations
      ? addMapping(state.organisations, sortedMappings)
      : state.mappedReturns,
    mappedOrganisations: state.organisations
      ? addEntitiesMapping(state.organisations, sortedMappings)
      : state.mappedOrganisations,
  };
}

export function handleUploadOrganisationAttachmentSuccess(
  state: OrganisationState,
  action: UploadOrganisationAttachmentSuccessAction
): OrganisationState {
  const { organisationId, attachment } = action.payload;

  // Update the organisation in the organisations list by adding the new attachment
  let organisations = state.organisations;
  if (organisations) {
    organisations = organisations.map((org) => {
      if (org.uuid === organisationId) {
        return {
          ...org,
          organisationDetails: {
            ...org.organisationDetails,
            attachments: [
              ...(org.organisationDetails?.attachments || []),
              attachment,
            ],
          },
        };
      }
      return org;
    });
  }

  return {
    ...state,
    loading: false,
    organisations,
  };
}

export function handleCompleteScanStepsSuccess(
  state: OrganisationState,
  action: CompleteScanStepsSuccessAction
): OrganisationState {
  const { organisationId } = action.payload;
  if (!state.organisations) return state;

  const organisations = state.organisations.map((org) =>
    org.uuid === organisationId
      ? { ...org, receiptScanStepsCompleted: true }
      : org
  );

  const mappings = state.mappings;
  const mappedReturns = mappings
    ? addMapping(organisations, mappings)
    : state.mappedReturns;
  const mappedOrganisations = mappings
    ? addEntitiesMapping(organisations, mappings)
    : state.mappedOrganisations;

  return {
    ...state,
    loading: false,
    organisations,
    mappedOrganisations,
    mappedReturns,
  };
}

/** Uploaded .xlsx files are legacy Excel imports; treat as reviewed in the file panel. */
